import React from 'react';

/**
 * Seiten-Hintergrund: dezenter Verlauf im Hellmodus, weiche Lichtschleier im Dunkeln.
 *
 * BEWUSST STATISCH: Die Schleier haben bis August 2026 dauerhaft geatmet
 * (`animate-aura-breathe`, 4s, endlos). Auf dem Desktop sind die Kreise 55–85vw
 * gross — die Bewegung war dort im Randblick permanent sichtbar und stoerend.
 * Ein Seiten-Hintergrund, der nie zur Ruhe kommt, zieht Aufmerksamkeit von genau
 * dem ab, was der Nutzer eigentlich liest. Bewegung bleibt dort, wo sie Bedeutung
 * traegt: in der Fokus-Session (`focus/PulsatingAura.js`, seit Sep 2026 eine
 * Canvas-Szene). Die alten `aura-breathe`-Keyframes sind aus tailwind.config.js
 * entfernt; wer den Hintergrund wieder atmen lassen will, legt sie neu an.
 */
const AuraLightBackground = ({
  className = '',
  intensity = 'normal', // "light", "normal", "strong"
}) => {
  // Hellmodus: Token-Verlauf (primary/accent) statt fester purple/blue/indigo-Werte,
  // damit der Hintergrund allen Theme-Achsen folgt.
  // Deckkraft auf rund ein Drittel gesenkt (Aug 2026): Der Verlauf toente vorher
  // die gesamte Flaeche sichtbar violett-blau, wodurch weisse Karten kaum noch
  // Kante gegen den Grund hatten und die App insgesamt bunt wirkte. Als Hauch
  // bleibt der Aura-Schimmer erhalten, ohne sich in den Vordergrund zu draengen.
  const getIntensitySettings = () => {
    switch (intensity) {
      case 'light':
        return 'from-primary/[0.03] via-accent/[0.02] to-primary/[0.03]';
      case 'strong':
        return 'from-primary/10 via-accent/[0.07] to-primary/10';
      default:
        return 'from-primary/5 via-accent/[0.03] to-primary/5';
    }
  };

  const baseGradient = getIntensitySettings();

  // Dunkelmodus: Deckkraft der Lichtschleier.
  const auroraOpacity = intensity === 'strong' ? 0.26 : intensity === 'light' ? 0.1 : 0.17;

  // Radial-Glow aus einem Token — folgt Dark/Kontrast, statt feste rgba-Werte zu setzen.
  const glow = (token, opacity) => ({
    background: `radial-gradient(circle, rgb(var(${token}) / ${opacity}) 0%, rgb(var(${token}) / 0) 70%)`,
  });

  return (
    <div className={`fixed inset-0 pointer-events-none overflow-hidden ${className}`}>
      {/* Hellmodus: dezenter Verlauf (im Dunkeln ausgeblendet) */}
      <div className={`absolute inset-0 bg-gradient-to-br ${baseGradient} transition-opacity duration-700 dark:opacity-0`} />

      {/* Dunkelmodus: weiche Lichtschleier (nur im Dark sichtbar) */}
      <div className="absolute inset-0 opacity-0 transition-opacity duration-700 dark:opacity-100">
        <div
          className="absolute -top-1/3 -left-1/4 w-[85vw] h-[85vw] rounded-full blur-3xl"
          style={glow('--color-primary', auroraOpacity)}
        />
        <div
          className="absolute -bottom-1/3 -right-1/4 w-[80vw] h-[80vw] rounded-full blur-3xl"
          style={glow('--color-primary', auroraOpacity - 0.02)}
        />
        {/* dezenter Akzent-Schimmer mittig oben */}
        <div
          className="absolute -top-1/4 left-1/3 w-[55vw] h-[55vw] rounded-full blur-3xl"
          style={glow('--color-accent', auroraOpacity - 0.05)}
        />
      </div>

      {/* Filmkorn (Aug 2026) — die Textur, die die App von einer generierten
          Oberflaeche unterscheidet. Eine mathematisch glatte Verlaufsflaeche
          gibt es in der physischen Welt nirgends; drei Prozent Rauschen darueber
          reichen, damit der Hintergrund nach Papier statt nach Photoshop
          aussieht. Man soll es nicht bemerken, nur merken.

          Der Platz ist mit Bedacht gewaehlt: Diese Komponente ist die EINE
          Hintergrundebene der App und liegt unter allem Inhalt. Korn ueber
          Fliesstext kostet Lesbarkeit und bringt dort nichts.
          Rezept + Deckkraft je Theme: `.grain` in index.css. */}
      <div className="grain absolute inset-0" aria-hidden="true" />
    </div>
  );
};

// memo: rein props-abhaengig — verhindert Mit-Rendern bei jedem MainScreen-Render
export default React.memo(AuraLightBackground);
